/* Dispatch Review & Send modal (Phase 7 + editable nominations).
 *
 * Given a single TradeDocumentDispatch row, this modal lets the assignee
 * (or branch-manager / staff) review the generated PDF, EDIT its data-driven
 * fields, tweak the recipient email and fire the Send action:
 *   - PDF preview: fetched from the dispatch ``preview`` endpoint, which
 *     renders the PDF with the dispatch's stored field overrides applied — so
 *     the preview is byte-identical to what Send will email. Shown in an
 *     ``<iframe>`` via ``URL.createObjectURL`` (revoked on unmount / close).
 *   - Edit fields: an optional panel of the document's editable fields (deal
 *     table, remarks, recipient/att lines, payment terms, …). Commercial
 *     fields (price/quantity/contract/payment) are flagged. The verbatim legal
 *     terms are NOT editable — they're never exposed here. "Save & preview"
 *     persists only the fields that actually changed vs the default and
 *     re-renders the preview.
 *   - Recipients: a multi-email chip input pre-seeded with the counterparty's
 *     resolved primary contact. All addresses go in the TO list; CC stays
 *     trader + branch (resolved server-side).
 *   - Send: persists any unsaved edits first, then POSTs the recipient list to
 *     the dispatch send endpoint. On 400 / 403 / 409 / 502 the server's
 *     ``error`` text is surfaced inline (no provider / stack detail leaks).
 *
 * Layout is scoped to ``.drs-*`` classes; the recipient + edit blocks set their
 * own ``display`` explicitly so they don't inherit the unscoped global
 * ``form {}`` rule that leaks out of ``Auth/Login.css``.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faPaperPlane,
  faExclamationTriangle,
  faPen,
  faChevronDown,
  faChevronUp,
} from '@fortawesome/free-solid-svg-icons';
import fuelEnquiryService from '../../services/fuelEnquiryService';
import { getErrorMessage } from '../../utils/errorMessages';
import './DispatchReviewSendModal.css';

// Pragmatic single-line email shape check (mirrors the browser ``type=email``
// rule closely enough for our needs). The backend re-validates every address
// with Django's ``validate_email`` as the source of truth.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = (value) => EMAIL_RE.test((value || '').trim());

// Fields that read better as multi-line textareas (longer prose / multi-product
// values). Everything else renders as a single-line input.
const MULTILINE_KEYS = new Set([
  'intro', 'comments', 'products_comments', 'shipment_comments',
  'payment', 'payment_terms', 'buyer',
  'product_names', 'volume_quantity', 'unit_price', 'unit_and_price',
]);

// Stable JSON for an overrides map so two equal maps compare equal regardless
// of key insertion order (used to detect "unsaved changes").
const normalizeOverrides = (obj) => {
  const keys = Object.keys(obj || {}).sort();
  return JSON.stringify(keys.map((k) => [k, obj[k]]));
};

// Default API: the bunkers fuel-enquiry dispatch endpoints. A caller for a
// different document source (the Freight Recap screen) passes its own ``api``
// prop with these four signatures — the modal itself stays source-agnostic.
const DEFAULT_API = {
  previewPdf: (dispatchId) => fuelEnquiryService.dispatchPreviewPdf(dispatchId),
  editableFields: (dispatchId) => fuelEnquiryService.dispatchEditableFields(dispatchId),
  saveOverrides: (dispatchId, overrides) => fuelEnquiryService.saveDispatchOverrides(dispatchId, overrides),
  send: (dispatchId, recipients) => fuelEnquiryService.sendDispatch(dispatchId, recipients),
};

const DispatchReviewSendModal = ({ isOpen, dispatch, onClose, onSent, api = null }) => {
  const svc = api || DEFAULT_API;
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  // ``recipients`` is the committed chip list; ``recipientDraft`` is the text
  // currently being typed (not yet committed to a chip).
  const [recipients, setRecipients] = useState([]);
  const [recipientDraft, setRecipientDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);

  // -- edit-fields state --------------------------------------------------
  const [fieldSpec, setFieldSpec] = useState([]); // [{key,label,sensitive}]
  const [defaults, setDefaults] = useState({});   // {key: default value}
  const [edits, setEdits] = useState({});         // {key: current value}
  const [editable, setEditable] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editLoadError, setEditLoadError] = useState(null);
  const [savedKey, setSavedKey] = useState('[]'); // normalized last-saved overrides
  const [saving, setSaving] = useState(false);

  // Keep the live object URL in a ref so the cleanup effect can revoke it
  // without re-running every time the URL state changes.
  const objectUrlRef = useRef(null);

  const dispatchId = dispatch?.id || null;
  // Names the supplier on a per-supplier nomination, so the trader can see
  // which of a deal's letters they are about to send.
  const title = (dispatch?.doc_type_display || 'Document')
    + (dispatch?.supplier_name ? ` — ${dispatch.supplier_name}` : '');

  // The override map to persist: only the fields whose current value differs
  // from the computed default. Reverting a field to its default drops it.
  const currentOverrides = useMemo(() => {
    const out = {};
    Object.keys(edits).forEach((k) => {
      if ((edits[k] ?? '') !== (defaults[k] ?? '')) out[k] = edits[k];
    });
    return out;
  }, [edits, defaults]);

  const dirty = normalizeOverrides(currentOverrides) !== savedKey;
  const editCount = Object.keys(currentOverrides).length;

  // Fetch + build the preview blob for the dispatch (with stored overrides).
  // Revokes the prior object URL before replacing it. Returns a promise so the
  // save flow can await a refresh.
  const loadPreview = useRef(null);
  loadPreview.current = (dispId) => {
    setPdfLoading(true);
    setPdfError(null);
    return svc.previewPdf(dispId)
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = url;
        setPdfUrl(url);
      })
      .catch((err) => {
        console.error('Dispatch PDF preview failed:', err);
        setPdfError('Could not load the document preview. You can still send it.');
      })
      .finally(() => setPdfLoading(false));
  };

  // On open (or when a different dispatch opens): seed recipients, load the
  // editable fields, and load the preview.
  useEffect(() => {
    if (!isOpen || !dispatch || !dispatchId) return undefined;

    let cancelled = false;
    const seed = (dispatch.resolved_recipient_email || '').trim();
    setRecipients(seed ? [seed] : []);
    setRecipientDraft('');
    setSendError(null);
    // The split layout gives the editor its own rail, so the fields open by
    // default — the collapsed toggle used to bury them under the preview.
    setEditOpen(true);
    setEditLoadError(null);

    svc.editableFields(dispatchId)
      .then((data) => {
        if (cancelled) return;
        const spec = (data?.fields || []).map(
          ({ key, label, sensitive }) => ({ key, label, sensitive }));
        const defs = {};
        (data?.fields || []).forEach((f) => { defs[f.key] = f.value ?? ''; });
        const saved = data?.overrides || {};
        const seededEdits = {};
        spec.forEach(({ key }) => {
          seededEdits[key] = Object.prototype.hasOwnProperty.call(saved, key)
            ? saved[key]
            : (defs[key] ?? '');
        });
        setFieldSpec(spec);
        setDefaults(defs);
        setEdits(seededEdits);
        setEditable(data?.editable !== false);
        setSavedKey(normalizeOverrides(saved));
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Dispatch editable-fields load failed:', err);
        setEditLoadError('Could not load the editable fields for this document.');
      });

    loadPreview.current(dispatchId);

    return () => { cancelled = true; };
  }, [isOpen, dispatchId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Revoke the object URL on unmount so the blob is not leaked.
  useEffect(() => () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  const handleClose = () => {
    if (sending || saving) return;
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setPdfUrl(null);
    setPdfError(null);
    setSendError(null);
    onClose();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  const setFieldValue = (key, value) => {
    setEdits((prev) => ({ ...prev, [key]: value }));
  };

  // Persist the current overrides, then refresh the preview so it matches.
  const handleSaveAndPreview = async () => {
    if (!editable) return;
    setSaving(true);
    setSendError(null);
    try {
      await svc.saveOverrides(dispatchId, currentOverrides);
      setSavedKey(normalizeOverrides(currentOverrides));
      await loadPreview.current(dispatchId);
    } catch (err) {
      setSendError(getErrorMessage(err, 'Could not save your edits. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  // Commit the current draft into a recipient chip. Returns true when the
  // draft was a valid, non-duplicate address (and was added or already
  // present); false when it was invalid so the caller can surface an error.
  const commitDraft = () => {
    const trimmed = recipientDraft.trim().replace(/,$/, '').trim();
    if (!trimmed) return true; // nothing to commit -> not an error
    if (!isValidEmail(trimmed)) {
      setSendError(`'${trimmed}' is not a valid email address.`);
      return false;
    }
    setRecipients((prev) => (
      prev.includes(trimmed) ? prev : [...prev, trimmed]
    ));
    setRecipientDraft('');
    setSendError(null);
    return true;
  };

  const handleRecipientKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commitDraft();
    } else if (e.key === 'Backspace' && !recipientDraft && recipients.length) {
      // Backspace on an empty draft pops the last chip (standard chip UX).
      setRecipients((prev) => prev.slice(0, -1));
    }
  };

  const removeRecipient = (email) => {
    setRecipients((prev) => prev.filter((r) => r !== email));
  };

  const handleSend = async () => {
    // Fold any in-progress draft into a chip first; bail if it's invalid.
    if (!commitDraft()) return;
    const draftTail = recipientDraft.trim().replace(/,$/, '').trim();
    const list = (draftTail && isValidEmail(draftTail) && !recipients.includes(draftTail))
      ? [...recipients, draftTail]
      : recipients;
    if (list.length === 0) {
      setSendError('At least one recipient email is required before sending.');
      return;
    }
    setSending(true);
    setSendError(null);
    try {
      // Persist any unsaved edits first so the emailed PDF matches the preview.
      if (editable && dirty) {
        await svc.saveOverrides(dispatchId, currentOverrides);
        setSavedKey(normalizeOverrides(currentOverrides));
      }
      await svc.send(dispatchId, list);
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
      setPdfUrl(null);
      if (onSent) onSent();
      onClose();
    } catch (err) {
      // ``getErrorMessage`` reads ``data.error`` first -- exactly the shape the
      // dispatch endpoints return for 400 / 403 / 409 / 502, so we never expose
      // anything beyond the server's own message.
      setSendError(getErrorMessage(err, 'Could not send the document. Please try again.'));
    } finally {
      setSending(false);
    }
  };

  if (!isOpen || !dispatch) return null;

  // Send is allowed when there's at least one committed chip, or a valid
  // draft about to be committed on send.
  const draftTail = recipientDraft.trim().replace(/,$/, '').trim();
  const hasSendableRecipient = (
    recipients.length > 0 || (!!draftTail && isValidEmail(draftTail))
  );
  const busy = sending || saving;
  const canSend = !busy && hasSendableRecipient;

  return (
    <div className="drs-modal-overlay" onMouseDown={handleOverlayClick}>
      <div className="drs-modal" role="dialog" aria-modal="true" aria-label={`Review and send ${title}`}>
        <div className="drs-modal-header">
          <h3 className="drs-modal-title">Review &amp; Send — {title}</h3>
          <button
            type="button"
            className="drs-modal-close"
            onClick={handleClose}
            disabled={busy}
            aria-label="Close"
            title="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Split body: PDF preview fills the left pane at full height; the
            editor, recipients, and errors live in a right rail that scrolls
            independently. The footer stays pinned below both, so Cancel /
            Send are always visible. */}
        <div className="drs-modal-body">
          <div className="drs-body-left">
          <div className="drs-preview">
            {pdfLoading && (
              <div className="drs-preview-state">Loading preview…</div>
            )}
            {!pdfLoading && pdfError && (
              <div className="drs-preview-state drs-preview-error">
                <FontAwesomeIcon icon={faExclamationTriangle} /> {pdfError}
              </div>
            )}
            {!pdfLoading && !pdfError && pdfUrl && (
              <iframe
                className="drs-preview-frame"
                title={`${title} preview`}
                src={pdfUrl}
              />
            )}
          </div>
          </div>

          <div className="drs-body-right">
          {/* Edit fields ----------------------------------------------------*/}
          {editable && fieldSpec.length > 0 && (
            <div className="drs-edit">
              <button
                type="button"
                className="drs-edit-toggle"
                onClick={() => setEditOpen((o) => !o)}
                aria-expanded={editOpen}
              >
                <span className="drs-edit-toggle-label">
                  <FontAwesomeIcon icon={faPen} /> Edit fields
                  {editCount > 0 && (
                    <span className="drs-edit-count">{editCount} edited</span>
                  )}
                </span>
                <FontAwesomeIcon icon={editOpen ? faChevronUp : faChevronDown} />
              </button>

              {editOpen && (
                <div className="drs-edit-panel">
                  <p className="drs-edit-hint">
                    Edit the document’s contents below, then Save &amp; preview.
                    The standard legal terms aren’t editable. Fields marked
                    <span className="drs-commercial-tag">Commercial</span> change
                    the binding deal — they’re logged on send.
                  </p>
                  <div className="drs-edit-grid">
                    {fieldSpec.map(({ key, label, sensitive }) => {
                      const value = edits[key] ?? '';
                      const changed = (value ?? '') !== (defaults[key] ?? '');
                      const multiline = MULTILINE_KEYS.has(key)
                        || (value && value.includes('\n'));
                      return (
                        <div
                          key={key}
                          className={`drs-field${sensitive ? ' drs-field-sensitive' : ''}${changed ? ' drs-field-changed' : ''}`}
                        >
                          <label className="drs-field-label" htmlFor={`drs-f-${key}`}>
                            {label}
                            {sensitive && (
                              <span className="drs-commercial-tag" title="Commercial term — change is logged on send">
                                Commercial
                              </span>
                            )}
                          </label>
                          {multiline ? (
                            <textarea
                              id={`drs-f-${key}`}
                              className="drs-field-input"
                              value={value}
                              rows={2}
                              onChange={(e) => setFieldValue(key, e.target.value)}
                              disabled={busy}
                            />
                          ) : (
                            <input
                              id={`drs-f-${key}`}
                              type="text"
                              className="drs-field-input"
                              value={value}
                              onChange={(e) => setFieldValue(key, e.target.value)}
                              disabled={busy}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="drs-edit-actions">
                    <button
                      type="button"
                      className="drs-btn drs-btn-secondary"
                      onClick={handleSaveAndPreview}
                      disabled={busy || !dirty}
                      title={!dirty ? 'No unsaved changes' : 'Save edits and refresh the preview'}
                    >
                      {saving ? 'Saving…' : 'Save & preview'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {editLoadError && (
            <div className="drs-edit-loaderror">
              <FontAwesomeIcon icon={faExclamationTriangle} /> {editLoadError}
            </div>
          )}

          <div className="drs-recipient">
            <label className="drs-recipient-label" htmlFor="drs-recipient-input">
              Recipients
            </label>
            <div className="drs-recipient-chips">
              {recipients.map((email) => (
                <span key={email} className="drs-chip" data-testid="drs-chip">
                  <span className="drs-chip-text">{email}</span>
                  <button
                    type="button"
                    className="drs-chip-remove"
                    onClick={() => removeRecipient(email)}
                    disabled={busy}
                    aria-label={`Remove ${email}`}
                    title={`Remove ${email}`}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </span>
              ))}
              <input
                id="drs-recipient-input"
                type="email"
                className="drs-recipient-input"
                value={recipientDraft}
                onChange={(e) => setRecipientDraft(e.target.value)}
                onKeyDown={handleRecipientKeyDown}
                onBlur={commitDraft}
                placeholder={recipients.length ? 'Add another…' : 'recipient@example.com'}
                disabled={busy}
                aria-label="Add recipient email"
              />
            </div>
            <div className="drs-recipient-hint">
              Press Enter or comma to add. All addresses are in the To line;
              your trader and branch are copied automatically.
            </div>
          </div>

          {sendError && (
            <div className="drs-send-error" role="alert">
              <FontAwesomeIcon icon={faExclamationTriangle} /> {sendError}
            </div>
          )}
          </div>
        </div>

        <div className="drs-modal-footer">
          <button
            type="button"
            className="drs-btn drs-btn-secondary"
            onClick={handleClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="drs-btn drs-btn-primary"
            onClick={handleSend}
            disabled={!canSend}
            title={!hasSendableRecipient ? 'Add a recipient email to send' : 'Send the document'}
          >
            <FontAwesomeIcon icon={faPaperPlane} />
            {sending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DispatchReviewSendModal;
